import { useState } from "react";

import { useCreateSource, useCurrentProjectId } from "@/api/queries";
import { Button } from "@/components/Button";
import { ErrorState } from "@/components/ErrorState";
import { Field, TextInput } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { useUiStore } from "@/store/ui";

export interface SourceCreateModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 登记资料（specs/14 §4.4 · `POST /projects/{id}/sources`）。
 *
 * `kind` 与 `rights_status` 用**自由输入**而不是下拉枚举：
 * 后端 `SourceIn` 对这两个字段没有取值约束（默认 `document` / `unknown`），
 * 前端凭空造一套枚举就违反了「不得硬编码后端未声明的枚举」（RULES §11）。
 * 占位符给的是数据里实际出现过的取值，仅作提示。
 */
export function SourceCreateModal({ open, onClose }: SourceCreateModalProps) {
  const projectId = useCurrentProjectId();
  const pushToast = useUiStore((state) => state.pushToast);
  const createSource = useCreateSource(projectId);

  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("document");
  const [uri, setUri] = useState("");
  const [rightsStatus, setRightsStatus] = useState("unknown");

  const reset = () => {
    setTitle("");
    setKind("document");
    setUri("");
    setRightsStatus("unknown");
  };

  const submit = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    createSource.mutate(
      {
        title: trimmed,
        kind: kind.trim() || "document",
        uri: uri.trim(),
        rights_status: rightsStatus.trim() || "unknown",
      },
      {
        onSuccess: () => {
          pushToast({
            title: "资料已登记",
            detail: "可在产物的「来源与依赖」里被引用",
            mark: "✓",
          });
          reset();
          onClose();
        },
        onError: (error) =>
          pushToast({
            title: "登记失败",
            detail: error instanceof Error ? error.message : "未知错误",
            mark: "!",
          }),
      },
    );
  };

  return (
    <Modal
      eyebrow="真相层"
      title="登记资料"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            id="submit-source"
            disabled={!title.trim() || createSource.isPending}
            onClick={submit}
          >
            {createSource.isPending ? "登记中…" : "登记"}
          </Button>
        </>
      }
    >
      <Field id="source-title" label="标题" required>
        <TextInput
          id="source-title"
          value={title}
          maxLength={300}
          placeholder="例如：《水浒传》相关章回（公共领域）"
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
        />
      </Field>
      <Field id="source-kind" label="类型" hint="后端未约束取值；常见：document / research">
        <TextInput
          id="source-kind"
          value={kind}
          onChange={(event) => setKind(event.target.value)}
        />
      </Field>
      <Field id="source-uri" label="位置" hint="可留空">
        <TextInput
          id="source-uri"
          value={uri}
          placeholder="例如：library://shuihu/chapters-23-26"
          onChange={(event) => setUri(event.target.value)}
        />
      </Field>
      <Field
        id="source-rights"
        label="权利状态"
        hint="未确认不得进入正式版本；常见：unknown / internal / public_domain"
      >
        <TextInput
          id="source-rights"
          value={rightsStatus}
          onChange={(event) => setRightsStatus(event.target.value)}
        />
      </Field>

      {createSource.isError ? <ErrorState title="登记失败" error={createSource.error} /> : null}
    </Modal>
  );
}
